/**
 * ============================================================================
 * СЕРВИС УРОКОВ
 * ============================================================================
 * Методы для работы с уроками
 * ============================================================================
 */

import api from './api'

export const lessonsService = {
	normalizePayload(lessonData) {
		const payload = { ...(lessonData || {}) }
		if (
			payload.enable_toc === undefined &&
			payload.enableToc !== undefined
		) {
			payload.enable_toc = payload.enableToc
		}
		if (payload.toc_mode === undefined && payload.tocMode !== undefined) {
			payload.toc_mode = payload.tocMode
		}
		if (
			payload.disable_main_materials_download === undefined &&
			payload.disableMainMaterialsDownload !== undefined
		) {
			payload.disable_main_materials_download =
				payload.disableMainMaterialsDownload
		}
		if (
			payload.disable_additional_materials_download === undefined &&
			payload.disableAdditionalMaterialsDownload !== undefined
		) {
			payload.disable_additional_materials_download =
				payload.disableAdditionalMaterialsDownload
		}
		return payload
	},
	/**
	 * Получить все уроки модуля
	 * @param {number} moduleId - ID модуля
	 * @param {boolean} includeUnpublished - Включать ли неопубликованные уроки (по умолчанию true для преподавателей)
	 */
	async getByModuleId(moduleId, includeUnpublished = true) {
		const response = await api.get(`/modules/${moduleId}/lessons`, {
			params: { includeUnpublished },
		})
		return response.data
	},

	/**
	 * Получить урок по ID
	 */
	async getById(id) {
		const response = await api.get(`/lessons/${id}`)
		return response.data
	},

	/**
	 * Создать новый урок
	 */
	async create(moduleId, lessonData) {
		const response = await api.post(
			`/modules/${moduleId}/lessons`,
			this.normalizePayload(lessonData)
		)
		return response.data
	},

	/**
	 * Обновить урок
	 */
	async update(id, lessonData) {
		const response = await api.put(
			`/lessons/${id}`,
			this.normalizePayload(lessonData)
		)
		return response.data
	},

	/**
	 * Удалить урок
	 */
	async delete(id) {
		const response = await api.delete(`/lessons/${id}`)
		return response.data
	},

	/**
	 * Изменить порядок уроков
	 */
	async reorder(moduleId, lessonIds) {
		const response = await api.post(`/modules/${moduleId}/lessons/reorder`, {
			lessonIds,
		})
		return response.data
	},

	/**
	 * Загрузить видео для урока
	 */
	async uploadVideo(lessonId, file) {
		const formData = new FormData()
		formData.append('video', file)

		const response = await api.post(`/lessons/${lessonId}/video`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})
		return response.data
	},

	/**
	 * Загрузить обложку видео для урока
	 */
	async uploadCover(lessonId, file) {
		const formData = new FormData()
		formData.append('cover', file)

		const response = await api.post(`/lessons/${lessonId}/cover`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})
		return response.data
	},

	/**
	 * Загрузить материалы для урока
	 */
	async uploadMaterials(lessonId, files) {
		const formData = new FormData()
		files.forEach(file => {
			formData.append('materials', file)
		})

		const response = await api.post(
			`/lessons/${lessonId}/materials`,
			formData,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}
		)
		return response.data
	},

	/**
	 * Удалить материал урока
	 * @param {number} lessonId - ID урока
	 * @param {number} materialId - ID материала
	 */
	async deleteMaterial(lessonId, materialId) {
		if (materialId === undefined || materialId === null) {
			throw new Error('materialId is required')
		}
		let normalizedMaterialId = String(materialId)
		try {
			normalizedMaterialId = decodeURIComponent(normalizedMaterialId)
		} catch (error) {
			// already decoded or partially malformed - отправим как есть после безопасного encode
		}
		normalizedMaterialId = encodeURIComponent(normalizedMaterialId)
		const response = await api.delete(
			`/lessons/${lessonId}/materials/${normalizedMaterialId}`
		)
		return response.data
	},

	/**
	 * Отметить урок как просмотренный
	 */
	async markAsCompleted(lessonId) {
		const response = await api.post(`/progress/lessons/${lessonId}/complete`)
		return response.data
	},

	async markCompleted(lessonId) {
		const response = await api.post(`/progress/lessons/${lessonId}/complete`)
		return response.data
	},

	/**
	 * Загрузить документ для урока (статья)
	 * @param {number} lessonId - ID урока
	 * @param {File} file - Файл документа (PDF, DOCX)
	 */
	async uploadDocument(lessonId, file) {
		const formData = new FormData()
		formData.append('document', file)

		const response = await api.post(
			`/lessons/${lessonId}/document`,
			formData,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}
		)
		return response.data
	},

	/**
	 * Удалить документ урока
	 * @param {number} lessonId - ID урока
	 */
	async deleteDocument(lessonId) {
		const response = await api.delete(`/lessons/${lessonId}/document`)
		return response.data
	},

	async uploadInlineMedia(lessonId, payload) {
		const formData = new FormData()
		if (payload?.videoFile) {
			formData.append('video', payload.videoFile)
		}
		if (payload?.coverFile) {
			formData.append('cover', payload.coverFile)
		}

		const response = await api.post(
			`/lessons/${lessonId}/inline-media`,
			formData,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}
		)
		return response.data
	},
}

export default lessonsService
